import React from 'react';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import {
    Box, Text, Button, Link, Image
} from 'components/ui';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import InfoButton from 'components/InfoButton/InfoButton';
import mediaUtils from 'utils/Media';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import Location from 'utils/Location';
import { trackForgotPasswordValidationError, trackForgotPasswordSendFailure, trackForgotPasswordEmailSent } from 'pages/Auth/ResetPassword/analytics';
import {
    colors, fontSizes, lineHeights, mediaQueries, space
} from 'style/config';
import ResetPasswordEN from 'pages/Auth/ResetPassword/locales/ResetPassword_en_US';
import ResetPasswordFR from 'pages/Auth/ResetPassword/locales/ResetPassword_fr_CA';
import { SCREENS, ERROR_CODES } from 'pages/Auth/ResetPassword/constants';

const { Media } = mediaUtils;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(ResetPasswordEN, ResetPasswordFR);

/**
 * PASSKEY_FIRST_LANDING — SIM-18 / ACS-4355 / SUB-TASK-3.
 *
 * Inline replacement for `ForgotPasswordModal`'s passkey-enabled render path.
 * Dual CTA: "Continue" (primary, dispatches `UserActions.forgotPassword(..., { pk: true })`)
 * + "Reset My Password" (secondary, dispatches with `source = 'orderConfirmation'`
 * on OC page, no `pk` option). Error branching mirrors ForgotPasswordModal:
 *   - ERR_PK_MAX_ATTEMPTS → inline `role="alert"` copy; no info-modal dispatch.
 *   - POS ERR_400004 → dispatch `showCheckYourEmailModal({ isOpen, email, isResetPasswordFlow })`
 *     — email is required (the modal reads props.email, no Redux fallback),
 *     matching the modal flow.
 *   - EMAIL_SEND_FAILED_CODE → inline `role="alert"` copy (AC-040 parity).
 *   - Generic → dispatch `showInfoModal(errorMsg)`.
 *
 * REUSE: `Button`, `InputEmail`, `InfoButton`, `Link`, `Text`, `Box`, `Image`,
 * `Media` — no hand-rolled primitives. Passkey icons match the modal
 * (`/img/ufe/passkey-modal-image[-desktop].svg`).
 */
const PasskeyFirstLanding = ({ onTransition, updateSharedState, sharedState }) => {
    const dispatch = useDispatch();
    const emailInput = useRef(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isValid = () => !emailInput.current?.validateError();

    const dispatchShowInfoModal = errorMsg => {
        if (!errorMsg) {
            return;
        }

        dispatch(
            Actions.showInfoModal({
                isOpen: true,
                title: getText('pageTitle'),
                message: errorMsg,
                buttonText: getText('confirmButton'),
                bodyPaddingBottom: 4,
                isHtml: true
            })
        );
    };

    const trackValidationError = () => {
        const emailError = emailInput.current?.validateError?.();
        trackForgotPasswordValidationError([emailError].filter(Boolean));
    };

    const handleFailure = (response, email) => {
        const errors = response?.errors || [];
        const errorCode = errors[0]?.errorCode;
        const errorMsg = response?.errorMessage || errors[0]?.errorMessage || getText('fallbackErrorMsg');

        flushSync(() => {
            setIsSubmitting(false);

            if (errorCode === ERROR_CODES.ERR_PK_MAX_ATTEMPTS) {
                setErrorMessage(getText('errorPasskeyMaxAttempts'));
            } else if (errorCode === ERROR_CODES.EMAIL_SEND_FAILED_CODE) {
                setErrorMessage(getText('emailSendFailed'));
            }
        });

        trackForgotPasswordSendFailure(response?.errorMessages);

        if (errorCode === ERROR_CODES.ERR_PK_MAX_ATTEMPTS || errorCode === ERROR_CODES.EMAIL_SEND_FAILED_CODE) {
            return;
        }

        if (errorCode === ERROR_CODES.POS_ACCOUNT_ERROR) {
            // CheckYourEmailModal reads the address from props.email (no Redux
            // fallback), so it must be passed here — matching the modal flow
            // (ForgotPasswordModal.ctrlr.jsx:156).
            dispatch(Actions.showCheckYourEmailModal({ isOpen: true, email, isResetPasswordFlow: true }));

            return;
        }

        dispatchShowInfoModal(errorMsg);
    };

    const submitPasskey = () => {
        if (!isValid()) {
            trackValidationError();

            return;
        }

        const email = emailInput.current.getValue();
        setErrorMessage('');
        setIsSubmitting(true);
        updateSharedState({ email, isPasskeyFlow: true });

        const onSuccess = () => {
            setIsSubmitting(false);
            trackForgotPasswordEmailSent(email);
            onTransition(SCREENS.EMAIL_SENT, { email, isPasskeyFlow: true });
        };
        dispatch(UserActions.forgotPassword(email, onSuccess, response => handleFailure(response, email), null, { pk: true }));
    };

    const submitTraditional = () => {
        if (!isValid()) {
            trackValidationError();

            return;
        }

        const email = emailInput.current.getValue();
        setErrorMessage('');
        setIsSubmitting(true);
        updateSharedState({ email, isPasskeyFlow: false });

        const source = Location.isOrderConfirmationPage() ? 'orderConfirmation' : null;
        const onSuccess = () => {
            setIsSubmitting(false);
            trackForgotPasswordEmailSent(email);
            onTransition(SCREENS.EMAIL_SENT, { email, isPasskeyFlow: false });
        };
        dispatch(UserActions.forgotPassword(email, onSuccess, response => handleFailure(response, email), source, {}));
    };

    const openPasskeyInfo = () => {
        dispatch(Actions.showPasskeysInfoModal({ isOpen: true }));
    };

    return (
        <>
            <Box css={styles.iconRow}>
                <Media lessThan='md'>
                    <Image
                        display='block'
                        height={96}
                        marginBottom={4}
                        src='/img/ufe/passkey-modal-image.svg'
                        width={96}
                        alt=''
                    />
                </Media>
                <Media greaterThanOrEqual='md'>
                    <Image
                        display='block'
                        height={64}
                        marginBottom={4}
                        src='/img/ufe/passkey-modal-image-desktop.svg'
                        width={64}
                        alt=''
                    />
                </Media>
            </Box>

            <Text
                is='h1'
                fontSize={fontSizes.lg}
                fontWeight='bold'
                lineHeight={lineHeights.tight}
                marginBottom={3}
            >
                {getText('forgotYourPasswordTitle')}
            </Text>

            <Text
                is='p'
                fontSize={fontSizes.md}
                lineHeight={lineHeights.base}
                marginBottom={3}
            >
                {getText('passkeysIntroMessage')}
                <InfoButton
                    marginLeft={0}
                    onClick={openPasskeyInfo}
                    aria-label={getText('passkeysInfoLabel')}
                />
            </Text>

            <Text
                is='p'
                color={colors.gray}
                fontSize={fontSizes.sm}
                marginBottom={3}
            >
                {getText('sharedDeviceDisclaimer')}
            </Text>

            {errorMessage ? (
                <Box
                    role='alert'
                    css={styles.errorAlert}
                    marginBottom={3}
                >
                    {errorMessage}
                </Box>
            ) : null}

            <form
                noValidate
                onSubmit={e => {
                    e.preventDefault();
                    submitPasskey();
                }}
            >
                <Box marginBottom={3}>
                    <InputEmail
                        label={getText('emailPlaceholder')}
                        login={sharedState?.email || ''}
                        ref={c => {
                            if (c !== null) {
                                emailInput.current = c;
                            }
                        }}
                        hideAsterisk={true}
                    />
                </Box>

                <Text
                    is='p'
                    color={colors.gray}
                    fontSize={fontSizes.sm}
                    marginBottom={4}
                >
                    {getText('termsAndConditions')}
                    <Link
                        color={colors.blue}
                        underline={true}
                        href='/beauty/terms-of-use'
                    >
                        {getText('termsOfUse')}
                    </Link>
                    {getText('termsAndConditionsRest')}
                    <Link
                        color={colors.blue}
                        underline={true}
                        href='/beauty/privacy-policy'
                    >
                        {getText('privacyPolicy')}
                    </Link>
                    .
                </Text>

                <Box
                    display='flex'
                    flexDirection='column'
                    gap={space[3]}
                    css={{
                        [mediaQueries.mdMax]: { flexDirection: 'column-reverse' }
                    }}
                >
                    <Button
                        variant='primary'
                        type='submit'
                        width='100%'
                        paddingY={space[3]}
                        disabled={isSubmitting}
                    >
                        {getText('continueButton')}
                    </Button>
                    <Button
                        variant='secondary'
                        type='button'
                        width='100%'
                        paddingY={space[3]}
                        disabled={isSubmitting}
                        onClick={submitTraditional}
                    >
                        {getText('resetMyPasswordCta')}
                    </Button>
                </Box>
            </form>
        </>
    );
};

const styles = {
    iconRow: {
        textAlign: 'center'
    },
    // AC-023: colors.red on white is WCAG-AA (~5.3:1).
    errorAlert: {
        color: colors.red
    }
};

export default wrapFunctionalComponent(PasskeyFirstLanding, 'PasskeyFirstLanding');
